import { useAdminEmailActivity, type EmailActivitySummary } from '@fidalia/api';
import { Alert, Card, PageHeader, Skeleton } from '@fidalia/ui';
import { createFileRoute } from '@tanstack/react-router';
import { MdInfoOutline, MdOutgoingMail } from 'react-icons/md';

import { QueueStatus } from '@/features/emails/queue-status';
import { TableauEnvois } from '@/features/emails/tableau-envois';
import { errorMessage } from '@/lib/auth';

export const Route = createFileRoute('/_app/emails')({
  component: EmailsPage,
});

/**
 * Suivi des envois de messages patients.
 *
 * Le résumé de la plateforme, l'état de la file, puis tous les envois avec
 * leur statut et la réponse du serveur. Le détail par cabinet — serveur,
 * plafond horaire, relais — est sur la fiche du cabinet, onglet « Envois ».
 */
function EmailsPage() {
  const { data, error } = useAdminEmailActivity({ limit: 1 });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={<MdOutgoingMail />}
        title="File d’envoi"
        description="Les messages patients partent du serveur de chaque cabinet : voilà ce qui part, ce qui est reporté et ce qui est refusé."
      />

      {error ? <Alert tone="danger">{errorMessage(error, 'Chargement impossible.')}</Alert> : null}

      {/* L'état de la file tient la première colonne sur deux lignes ; les six
          chiffres du résumé se rangent à côté. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <QueueStatus className="col-span-2 lg:col-span-1 lg:row-span-2" />
        {data ? <Resume summary={data.summary} /> : <ResumeSqueletes />}
      </div>

      <hr className="border-border-subtle" />

      <TableauEnvois />
    </div>
  );
}

/**
 * Le résumé plateforme, en six chiffres.
 *
 * Les tuiles en « x / total » se lisent d'un coup d'œil : la part de messages
 * en échec ou reportés sur les 24 h, et la part de cabinets concernés.
 */
const AIDES: Record<string, string> = {
  'Messages envoyés (24 h)':
    'Messages envoyés avec succès sur les dernières 24 heures, tous cabinets confondus.',
  'Envois échoués (24 h)':
    'Messages refusés par le serveur d’envoi du cabinet (identifiants invalides, boîte pleine, adresse rejetée…), sur le total des messages traités en 24 h. Voir la réponse du serveur dans la liste ci-dessous.',
  'Envois reportés (24 h)':
    'Messages mis en attente puis repris plus tard (plafond horaire atteint ou serveur momentanément indisponible), sur le total traité en 24 h. Ils partiront d’eux-mêmes.',
  'Envoi noreply@fidalia.fr (24 h)':
    'Messages envoyés depuis notre propre domaine, sur l’ensemble des messages envoyés en 24 h. C’est un dépannage offert aux cabinets sans adresse d’envoi, limité à un forfait : une part élevée signale des cabinets à relancer.',
  'Sans e-mail connecté':
    'Cabinets actifs n’ayant pas connecté leur adresse mail d’envoi (SMTP / Gmail). Ils dépendent du relais Fidalia, puis ne peuvent plus envoyer une fois le forfait épuisé.',
  'Cabinets au plafond':
    'Cabinets ayant atteint leur plafond d’envois par heure : leurs messages suivants sont reportés. Le plafond se règle depuis la fiche du cabinet, onglet « Envois ».',
};

/** Pastille « i » : explique une tuile au survol ou au focus clavier. */
function Aide({ texte }: { texte: string }) {
  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        aria-label={texte}
        className="text-content-subtle hover:text-brand-600 focus-visible:text-brand-600 focus-visible:outline-none"
      >
        <MdInfoOutline aria-hidden="true" className="size-4" />
      </button>
      <span
        role="tooltip"
        className="pointer-events-none absolute top-full right-0 z-20 mt-1.5 hidden w-64 rounded-field border border-border-subtle bg-surface p-3 text-xs leading-relaxed font-normal text-content-muted shadow-card group-focus-within:block group-hover:block"
      >
        {texte}
      </span>
    </span>
  );
}

/** Les six tuiles, le temps que le résumé arrive : même gabarit, rien ne saute. */
function ResumeSqueletes() {
  return (
    <>
      {Array.from({ length: 6 }, (_, index) => (
        <Card key={index} className="flex flex-col gap-2 p-4" aria-busy="true">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-8 w-16" />
        </Card>
      ))}
    </>
  );
}

function Resume({ summary }: { summary: EmailActivitySummary }) {
  const traites = summary.sent24h + summary.failed24h + summary.pending24h;

  const tuiles = [
    {
      label: 'Sans e-mail connecté',
      valeur: summary.cabinetsWithoutSmtp,
      total: summary.cabinetsActifs,
      ton: 'neutre' as const,
    },
    {
      label: 'Messages envoyés (24 h)',
      valeur: summary.sent24h,
      ton: 'neutre' as const,
    },
    {
      label: 'Envois échoués (24 h)',
      valeur: summary.failed24h,
      total: traites,
      ton: summary.failed24h > 0 ? ('danger' as const) : ('neutre' as const),
    },
    {
      label: 'Envois reportés (24 h)',
      valeur: summary.pending24h,
      total: traites,
      ton: summary.pending24h > 0 ? ('brand' as const) : ('neutre' as const),
    },
    {
      label: 'Envoi noreply@fidalia.fr (24 h)',
      valeur: summary.relaySent24h,
      total: summary.sent24h,
      // Notre domaine porte ces envois : au-delà d'une marche d'escalier, ce
      // chiffre est un signal, pas une statistique.
      ton: summary.relaySent24h > 0 ? ('brand' as const) : ('neutre' as const),
    },
    {
      label: 'Cabinets au plafond',
      valeur: summary.cabinetsAtCap,
      total: summary.cabinetsActifs,
      ton: summary.cabinetsAtCap > 0 ? ('danger' as const) : ('neutre' as const),
    },
  ];

  return (
    <>
      {tuiles.map((tuile) => (
        <Card key={tuile.label} className="flex flex-col gap-1 p-4">
          <span className="flex items-center justify-between gap-2 text-xs text-content-muted">
            {tuile.label}
            {AIDES[tuile.label] ? <Aide texte={AIDES[tuile.label]!} /> : null}
          </span>
          <span
            className={
              tuile.ton === 'danger'
                ? 'text-2xl font-semibold text-danger tabular-nums'
                : tuile.ton === 'brand'
                  ? 'text-2xl font-semibold text-brand-600 tabular-nums'
                  : 'text-2xl font-semibold text-content tabular-nums'
            }
          >
            {tuile.valeur}
            {tuile.total !== undefined ? (
              <span className="text-sm font-normal text-content-subtle"> / {tuile.total}</span>
            ) : null}
          </span>
        </Card>
      ))}
    </>
  );
}
